import CategoryHeader, { CategoryHeaderProps } from '@/components/elements/CategoryHeader';
import { Fab } from '@/components/elements/forms/buttons';
import { FabProps } from '@/components/elements/forms/buttons/Fab/Fab';
import ItemPaginator from '@/components/elements/ItemPaginator';
import Icon from '@/components/icons/Icon';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { debounce } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

interface HorizontalScrollSectionProps {
  categoryHeader?: CategoryHeaderProps;
  description?: string;
  children: React.ReactNode;
  className?: string;
  showPagination?: boolean;
  showFadeOverlay?: boolean;
  scrollSnap?: boolean;
  scrollButtonVariant?: FabProps['variant'];
  logoImgUrl?: string;
  /**
   *  Auxiliary trailing card (e.g. "view all"). Rendered after `children`
   */
  trailingExtra?: React.ReactNode;
  scrollContainerClassName?: string;
}

const baseTranslationKey = 'features.deals.components.HorizontalScrollSection' as const;

const HorizontalScrollSection: React.FC<HorizontalScrollSectionProps> = ({
  categoryHeader,
  description,
  children,
  className,
  showPagination = false,
  showFadeOverlay = true,
  scrollSnap = false,
  scrollButtonVariant,
  logoImgUrl,
  trailingExtra,
  scrollContainerClassName,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const getScrollDistance = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 0;

    // Get the first child element to calculate its width + gap
    const firstChild = container.firstElementChild as HTMLElement;
    if (!firstChild) return container.clientWidth;

    // Get computed style to find the gap
    const computedStyle = window.getComputedStyle(container);
    const gap = parseFloat(computedStyle.gap) || 0;

    // Return the width of one card plus the gap
    return firstChild.offsetWidth + gap;
  }, []);

  /** How many full cards are visible in the scroll container at once */
  const getCardsPerPage = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 1;

    const firstChild = container.firstElementChild as HTMLElement;
    if (!firstChild) return 1;

    const computedStyle = window.getComputedStyle(container);
    const gap = parseFloat(computedStyle.gap) || 0;
    const cardWidth = firstChild.offsetWidth;

    return Math.max(1, Math.round((container.clientWidth + gap) / (cardWidth + gap)));
  }, []);

  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;

    setCanScrollLeft(!isAtStart);
    setCanScrollRight(!isAtEnd);

    // Compute page-based pagination: each "page" = cardsPerPage cards
    const cardScrollDistance = getScrollDistance();
    const perPage = getCardsPerPage();
    const childCount = container.children.length;
    const pages = Math.max(1, Math.ceil(childCount / perPage));
    setTotalPages(pages);

    if (cardScrollDistance > 0) {
      // When scrolled to the very end, force last page active
      // (max scrollLeft rarely lands exactly on the last page boundary)
      if (isAtEnd) {
        setActivePageIndex(pages - 1);
      } else {
        const pageScrollDistance = perPage * cardScrollDistance;
        const currentPage = Math.round(scrollLeft / pageScrollDistance);
        setActivePageIndex(Math.min(currentPage, pages - 1));
      }
    }
  }, [getScrollDistance, getCardsPerPage]);

  // Debounced version to reduce layout thrash during rapid scroll/resize
  const checkScrollDebounced = useMemo(
    () => debounce(checkScroll, 100, { leading: true, trailing: true }),
    [checkScroll],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      checkScrollDebounced.cancel();
    };
  }, [checkScrollDebounced]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScroll();

    container.addEventListener('scroll', checkScrollDebounced);
    window.addEventListener('resize', checkScrollDebounced);

    return () => {
      container.removeEventListener('scroll', checkScrollDebounced);
      window.removeEventListener('resize', checkScrollDebounced);
    };
  }, [checkScroll, checkScrollDebounced]);

  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardScrollDistance = getScrollDistance();
    const perPage = getCardsPerPage();
    const pageScrollDistance = perPage * cardScrollDistance;
    const currentPage = Math.round(container.scrollLeft / pageScrollDistance);
    const targetPage = Math.max(0, currentPage - 1);

    container.scrollTo({
      left: targetPage * pageScrollDistance,
      behavior: 'smooth',
    });
  }, [getScrollDistance, getCardsPerPage]);

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardScrollDistance = getScrollDistance();
    const perPage = getCardsPerPage();
    const pageScrollDistance = perPage * cardScrollDistance;
    const currentPage = Math.round(container.scrollLeft / pageScrollDistance);
    const targetPage = currentPage + 1;

    container.scrollTo({
      left: targetPage * pageScrollDistance,
      behavior: 'smooth',
    });
  }, [getScrollDistance, getCardsPerPage]);

  // Keyboard navigation handler for arrow keys
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollLeft();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollRight();
      }
    },
    [scrollLeft, scrollRight],
  );

  const needsScroll = canScrollLeft || canScrollRight;

  return (
    <section className={twMerge('relative isolate', className)}>
      {/* Header */}
      <SectionHeader
        categoryHeader={categoryHeader}
        onScrollLeft={scrollLeft}
        onScrollRight={scrollRight}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        needsScroll={needsScroll}
        scrollButtonVariant={scrollButtonVariant}
        logoImgUrl={logoImgUrl}
      />
      {description && <p className="pl-md mt-md md:mt-xs text-fg_secondary text-l">{description}</p>}
      {/* Scrollable Container with Navigation */}
      <div className="relative">
        {/* Left fade overlay */}
        {needsScroll && showFadeOverlay && (
          <div
            style={{ width: 20 }}
            className="from-surfaceColors-surface_l4 pointer-events-none absolute left-0 top-0 z-10 h-full to-transparent 2xl:bg-gradient-to-r"
            aria-hidden="true"
          />
        )}

        {/* Right fade overlay */}
        {needsScroll && showFadeOverlay && (
          <div
            style={{ width: 20 }}
            className="from-surfaceColors-surface_l4 pointer-events-none absolute right-0 top-0 z-10 h-full to-transparent 2xl:bg-gradient-to-l"
            aria-hidden="true"
          />
        )}

        {/* Scrollable Cards Container */}
        <div
          ref={scrollContainerRef}
          role="list"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={twMerge(
            'no-scrollbar gap-md scroll-px-md focus-visible:ring-fg_link flex overflow-x-scroll scroll-smooth focus-visible:outline-none focus-visible:ring-2',
            scrollSnap && 'snap-x snap-mandatory',
            scrollContainerClassName,
          )}>
          {children}
          {trailingExtra}
        </div>
      </div>

      {/* Pagination Dots */}
      {showPagination && totalPages > 1 && (
        <ItemPaginator
          totalItems={totalPages}
          activeIndex={activePageIndex}
          onNavigate={(pageIndex) => {
            const container = scrollContainerRef.current;
            if (!container) return;
            const cardScrollDistance = getScrollDistance();
            const perPage = getCardsPerPage();
            container.scrollTo({
              left: pageIndex * perPage * cardScrollDistance,
              behavior: 'smooth',
            });
          }}
        />
      )}
    </section>
  );
};

const SectionHeader = ({
  categoryHeader,
  onScrollLeft,
  onScrollRight,
  canScrollLeft,
  canScrollRight,
  needsScroll,
  scrollButtonVariant,
  logoImgUrl,
}: {
  categoryHeader?: CategoryHeaderProps;
  onScrollLeft: () => void;
  onScrollRight: () => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  needsScroll: boolean;
  scrollButtonVariant?: FabProps['variant'];
  logoImgUrl?: string;
}) => {
  const { isMobileView } = useMobileView();

  if (isMobileView) {
    return <CategoryHeader {...categoryHeader} className="px-md items-center justify-between md:items-baseline" />;
  }

  return (
    <div className="space-x-xs px-md flex w-full items-baseline justify-between">
      <CategoryHeader {...categoryHeader} />
      <div className="gap-md flex flex-shrink-0 items-center">
        {logoImgUrl && (
          <Fragment>
            <span className="sr-only">{_t('showAll')}</span>
            {categoryHeader?.link?.to ? (
              <Link to={categoryHeader.link.to}>
                <img src={logoImgUrl} height={40} width={80} alt="" />
              </Link>
            ) : (
              <img src={logoImgUrl} height={40} width={80} alt="" />
            )}
          </Fragment>
        )}
        {needsScroll && (
          <div className="space-x-sm flex items-center">
            <Fab
              aria-label={_t(`${baseTranslationKey}.scrollLeft`)}
              onClick={onScrollLeft}
              icon={<Icon variant="chevron-left" />}
              size="sm"
              variant={scrollButtonVariant ?? 'secondary'}
              disabled={!canScrollLeft}
            />
            <Fab
              aria-label={_t(`${baseTranslationKey}.scrollRight`)}
              onClick={onScrollRight}
              icon={<Icon variant="chevron-right" />}
              size="sm"
              variant={scrollButtonVariant ?? 'secondary'}
              disabled={!canScrollRight}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalScrollSection;
